import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  request,
  type V3ApiError,
  type V3ApiSuccessData,
} from 'javascripts/services/v3ApiClient';
import type {
  TaskObject,
  TaskImageObject,
  TaskVideoObject,
  AssignedUserObject,
} from 'data/v3/data-contracts';
import { ProjectDatabaseId } from 'data/v1';
import {
  GetProjectTasksResponse,
  PROJECT_TASKS_QUERY_KEY,
} from './useGetProjectTasks';
import {
  GetAssetProjectTasksResponse,
  ASSET_PROJECT_TASKS_QUERY_KEY,
} from './useGetAssetProjectTasks';

type UpdateProjectTaskResponse = V3ApiSuccessData<TaskObject>;

type UpdateProjectTaskParams = {
  task: {
    id: TaskObject['id'];
    details: string;
    position: number;
    status?: string;
    uuid?: string;
    assignees?: {
      id: AssignedUserObject['id'];
      _destroy?: boolean;
    }[];
  };
};

export const useUpdateProjectTask = ({
  projectDbId,
  assetDbId,
  onSuccess,
  onError,
}: {
  projectDbId: ProjectDatabaseId;
  assetDbId?: TaskImageObject['id'] | TaskVideoObject['id'];
  onSuccess?: ({
    updatedTask,
    updatedAction,
  }: {
    updatedTask: TaskObject;
    updatedAction?: string;
  }) => void;
  onError?: (error: V3ApiError) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateProjectTaskResponse,
    V3ApiError,
    UpdateProjectTaskParams
  >({
    mutationFn: async (params: UpdateProjectTaskParams) => {
      const { id } = params.task;
      const response = await request({
        url: `/projects/${projectDbId}/tasks/${id}`,
        method: 'put',
        data: params,
      });
      return response.data;
    },
    onMutate: async (params: UpdateProjectTaskParams) => {
      const { id: taskDbId } = params.task;

      await queryClient.cancelQueries({
        queryKey: [PROJECT_TASKS_QUERY_KEY, projectDbId],
      });

      const projectTasksData =
        queryClient.getQueryData<GetProjectTasksResponse>([
          PROJECT_TASKS_QUERY_KEY,
          projectDbId,
        ]);

      if (projectTasksData) {
        const existingTasks = projectTasksData.data.tasks || [];

        queryClient.setQueryData([PROJECT_TASKS_QUERY_KEY, projectDbId], {
          data: {
            ...projectTasksData.data,
            tasks: existingTasks.map((task: TaskObject) => {
              // Optimistically update task
              if (task.id === taskDbId) {
                const usersToRemove = params.task.assignees.filter(
                  (assignee) => assignee._destroy,
                );

                // Filter out removed users from the current task's assigned users
                const updatedAssignedUsers = (task.assignedUsers || []).filter(
                  (user) =>
                    !usersToRemove.some(
                      (removedUser) => removedUser.id === user.id,
                    ),
                );

                // Add back the users who are not marked for removal
                const newAssignees = params.task.assignees.filter(
                  (assignee) => !assignee._destroy,
                );

                return {
                  ...params.task,
                  assignedUsers: [...updatedAssignedUsers, ...newAssignees],
                };
              }

              return task;
            }),
          },
        });
      }

      // Update cache for asset project task
      if (assetDbId) {
        await queryClient.cancelQueries({
          queryKey: [ASSET_PROJECT_TASKS_QUERY_KEY, assetDbId],
        });

        const assetProjectTaskData =
          await queryClient.getQueryData<GetAssetProjectTasksResponse>([
            ASSET_PROJECT_TASKS_QUERY_KEY,
            assetDbId,
          ]);

        if (assetProjectTaskData) {
          const existingAssetTasks = assetProjectTaskData.data;

          queryClient.setQueryData([ASSET_PROJECT_TASKS_QUERY_KEY, assetDbId], {
            data: existingAssetTasks.map((task: TaskObject) => {
              if (task.id === taskDbId) {
                return params.task;
              }

              return task;
            }),
          });
        }
      }
    },
    onSuccess: ({ data }, variables) => {
      const { assignees } = variables.task;
      let updatedAction;

      if (!assignees || assignees.length === 0) {
        updatedAction = undefined;
      } else if (assignees[0]?._destroy) {
        updatedAction = 'unassigned_user';
      } else {
        updatedAction = 'assigned_user';
      }

      onSuccess?.({
        updatedTask: data,
        updatedAction,
      });
    },
    onError: (e) => {
      onError?.(e);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [PROJECT_TASKS_QUERY_KEY, projectDbId],
      });
      queryClient.invalidateQueries({
        queryKey: [ASSET_PROJECT_TASKS_QUERY_KEY, assetDbId],
      });
    },
  });
};

export default useUpdateProjectTask;
